import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import {
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Box,
  Typography,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { InputAdornment, IconButton } from "@material-ui/core";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  composeOpenAiPromptFromSections,
  DEFAULT_HANDOFF_AI_TRIGGER,
  DEFAULT_HANDOFF_KEYWORDS,
  DEFAULT_HANDOFF_MESSAGE,
} from "../../utils/composeOpenAiPrompt";

const allowedModels = [
  "gpt-3.5-turbo-1106",
  "gpt-4o",
  "gemini-1.5-flash",
  "gemini-1.5-pro",
  "gemini-2.0-flash",
  "gemini-2.0-pro",
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  dialogContent: {
    minHeight: 420,
    maxHeight: "65vh",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  sectionHint: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.5,
  },
  sectionCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border:
      theme.mode === "light"
        ? "1px solid rgba(15, 23, 42, 0.08)"
        : "1px solid rgba(148, 163, 184, 0.12)",
  },
  sectionLabel: {
    fontWeight: 600,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(0.75),
  },
  tabs: {
    borderBottom:
      theme.mode === "light"
        ? "1px solid rgba(15, 23, 42, 0.08)"
        : "1px solid rgba(148, 163, 184, 0.12)",
    marginBottom: theme.spacing(2),
  },
}));

const DialogflowSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("flowOpenAiModal.validation.nameMin"))
    .max(100, i18n.t("flowOpenAiModal.validation.nameMax"))
    .required(i18n.t("common.required")),
  model: Yup.string()
    .oneOf(allowedModels, i18n.t("flowOpenAiModal.validation.model"))
    .required(i18n.t("flowOpenAiModal.validation.modelRequired")),
  maxTokens: Yup.number()
    .min(10, i18n.t("flowOpenAiModal.validation.tokensMin"))
    .max(4096, i18n.t("flowOpenAiModal.validation.tokensMax"))
    .required(i18n.t("common.required")),
  temperature: Yup.number()
    .min(0, i18n.t("flowOpenAiModal.validation.tempMin"))
    .max(1, i18n.t("flowOpenAiModal.validation.tempMax"))
    .required(i18n.t("common.required")),
  apiKey: Yup.string().required(i18n.t("flowOpenAiModal.validation.apiKey")),
  maxMessages: Yup.number()
    .min(1, i18n.t("flowOpenAiModal.validation.messagesMin"))
    .max(50, i18n.t("flowOpenAiModal.validation.messagesMax"))
    .required(i18n.t("common.required")),
  voice: Yup.string().notRequired(),
  voiceKey: Yup.string().notRequired(),
  voiceRegion: Yup.string().notRequired(),
  prompt: Yup.string().notRequired(),
  promptRole: Yup.string().notRequired(),
  promptRules: Yup.string().notRequired(),
  promptKnowledge: Yup.string().notRequired(),
  promptRestrictions: Yup.string().notRequired(),
  promptExamples: Yup.string().notRequired(),
  handoffKeywords: Yup.string().notRequired(),
  handoffAiTrigger: Yup.string().notRequired(),
  handoffMessage: Yup.string().notRequired(),
});

const PromptSectionField = ({ name, label, helper, rows = 4 }) => (
  <Box className="section-card-wrapper" mb={2}>
    <Typography className="section-label">{label}</Typography>
    {helper && (
      <Typography className="section-hint" style={{ marginBottom: 8, fontSize: "0.8125rem", color: "#64748b" }}>
        {helper}
      </Typography>
    )}
    <Field
      as={TextField}
      name={name}
      variant="outlined"
      margin="dense"
      fullWidth
      multiline
      rows={rows}
    />
  </Box>
);

const FlowBuilderOpenAIModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const [activeTab, setActiveTab] = useState(0);
  const [queues, setQueues] = useState([]);

  const initialState = {
    name: "",
    prompt: "",
    promptRole: "",
    promptRules: "",
    promptKnowledge: "",
    promptRestrictions: "",
    promptExamples: "",
    model: "gpt-4o",
    voice: "texto",
    voiceKey: "",
    voiceRegion: "",
    maxTokens: 800,
    temperature: 1,
    apiKey: "",
    maxMessages: 10,
    queueId: 0,
    handoffEnabled: true,
    handoffKeywords: DEFAULT_HANDOFF_KEYWORDS,
    handoffAiTrigger: DEFAULT_HANDOFF_AI_TRIGGER,
    handoffMessage: DEFAULT_HANDOFF_MESSAGE,
    handoffMode: "flow",
  };

  const [showApiKey, setShowApiKey] = useState(false);
  const [integration, setIntegration] = useState(initialState);
  const [labels, setLabels] = useState({
    title: i18n.t("flowOpenAiModal.title.add"),
    btn: i18n.t("flowOpenAiModal.buttons.add"),
  });

  useEffect(() => {
    (async () => {
      try {
        const { data: queueData } = await api.get("/queue");
        setQueues(queueData || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: i18n.t("flowOpenAiModal.title.edit"),
        btn: i18n.t("flowOpenAiModal.buttons.save"),
      });
      const typebotIntegration = data?.data?.typebotIntegration || {};
      const hasSections = [
        typebotIntegration.promptRole,
        typebotIntegration.promptRules,
        typebotIntegration.promptKnowledge,
        typebotIntegration.promptRestrictions,
        typebotIntegration.promptExamples,
      ].some(Boolean);

      setIntegration({
        ...initialState,
        ...typebotIntegration,
        promptKnowledge:
          !hasSections && typebotIntegration.prompt
            ? typebotIntegration.prompt
            : typebotIntegration.promptKnowledge || "",
        model: allowedModels.includes(typebotIntegration.model)
          ? typebotIntegration.model
          : "gpt-4o",
        handoffEnabled: typebotIntegration.handoffEnabled !== false,
        handoffMode: typebotIntegration.handoffMode || "flow",
        queueId: typebotIntegration.queueId || 0,
      });
    } else if (open === "create") {
      setLabels({
        title: i18n.t("flowOpenAiModal.title.add"),
        btn: i18n.t("flowOpenAiModal.buttons.add"),
      });
      setIntegration(initialState);
      setActiveTab(0);
    }

    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data]);

  const handleClose = () => {
    close(null);
  };

  const handleSavePrompt = (values, { setSubmitting }) => {
    const composedPrompt = composeOpenAiPromptFromSections(values);

    if (!composedPrompt || composedPrompt.length < 20) {
      toast.error(i18n.t("flowOpenAiModal.validation.promptMin"));
      setSubmitting(false);
      return;
    }

    const promptData = {
      ...values,
      prompt: composedPrompt,
      voice: values.model === "gpt-3.5-turbo-1106" ? values.voice : "texto",
      queueId: Number(values.queueId) || 0,
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: { typebotIntegration: promptData },
      });
    } else if (open === "create") {
      promptData.projectName = promptData.name;
      onSave({
        typebotIntegration: promptData,
      });
    }
    handleClose();
    setSubmitting(false);
  };

  const renderGeneralTab = ({ touched, errors, values, setFieldValue }) => (
    <>
      <Field
        as={TextField}
        label={i18n.t("promptModal.form.name")}
        name="name"
        error={touched.name && Boolean(errors.name)}
        helperText={touched.name && errors.name}
        variant="outlined"
        margin="dense"
        fullWidth
        required
      />
      <FormControl fullWidth margin="dense" variant="outlined">
        <Field
          as={TextField}
          label={i18n.t("promptModal.form.apikey")}
          name="apiKey"
          type={showApiKey ? "text" : "password"}
          error={touched.apiKey && Boolean(errors.apiKey)}
          helperText={touched.apiKey && errors.apiKey}
          variant="outlined"
          margin="dense"
          fullWidth
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowApiKey(!showApiKey)}>
                  {showApiKey ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>
      <div className={classes.multFieldLine}>
        <FormControl fullWidth margin="dense" variant="outlined" error={touched.model && Boolean(errors.model)}>
          <InputLabel>{i18n.t("promptModal.form.model")}</InputLabel>
          <Field
            as={Select}
            label={i18n.t("promptModal.form.model")}
            name="model"
            onChange={(e) => {
              setFieldValue("model", e.target.value);
              if (e.target.value !== "gpt-3.5-turbo-1106") {
                setFieldValue("voice", "texto");
              }
            }}
          >
            {allowedModels.map((model) => (
              <MenuItem key={model} value={model}>
                {model}
              </MenuItem>
            ))}
          </Field>
        </FormControl>
        <FormControl
          fullWidth
          margin="dense"
          variant="outlined"
          disabled={values.model !== "gpt-3.5-turbo-1106"}
        >
          <InputLabel>{i18n.t("promptModal.form.voice")}</InputLabel>
          <Field as={Select} label={i18n.t("promptModal.form.voice")} name="voice">
            <MenuItem value="texto">Texto</MenuItem>
            <MenuItem value="pt-BR-FranciscaNeural">Francisca</MenuItem>
            <MenuItem value="pt-BR-AntonioNeural">Antônio</MenuItem>
          </Field>
        </FormControl>
      </div>
      <div className={classes.multFieldLine}>
        <Field
          as={TextField}
          label={i18n.t("promptModal.form.temperature")}
          name="temperature"
          variant="outlined"
          margin="dense"
          fullWidth
          type="number"
          inputProps={{ step: "0.1", min: "0", max: "1" }}
        />
        <Field
          as={TextField}
          label={i18n.t("promptModal.form.max_tokens")}
          name="maxTokens"
          variant="outlined"
          margin="dense"
          fullWidth
          type="number"
        />
        <Field
          as={TextField}
          label={i18n.t("promptModal.form.max_messages")}
          name="maxMessages"
          variant="outlined"
          margin="dense"
          fullWidth
          type="number"
        />
      </div>
    </>
  );

  const renderPromptTab = () => (
    <>
      <Typography className={classes.sectionHint}>
        {i18n.t("flowOpenAiModal.sections.hint")}
      </Typography>
      <PromptSectionField
        name="promptRole"
        label={i18n.t("flowOpenAiModal.sections.role")}
        helper={i18n.t("flowOpenAiModal.sections.roleHint")}
        rows={3}
      />
      <PromptSectionField
        name="promptRules"
        label={i18n.t("flowOpenAiModal.sections.rules")}
        helper={i18n.t("flowOpenAiModal.sections.rulesHint")}
        rows={4}
      />
      <PromptSectionField
        name="promptKnowledge"
        label={i18n.t("flowOpenAiModal.sections.knowledge")}
        helper={i18n.t("flowOpenAiModal.sections.knowledgeHint")}
        rows={6}
      />
      <PromptSectionField
        name="promptRestrictions"
        label={i18n.t("flowOpenAiModal.sections.restrictions")}
        helper={i18n.t("flowOpenAiModal.sections.restrictionsHint")}
        rows={3}
      />
      <PromptSectionField
        name="promptExamples"
        label={i18n.t("flowOpenAiModal.sections.examples")}
        helper={i18n.t("flowOpenAiModal.sections.examplesHint")}
        rows={4}
      />
    </>
  );

  const renderHandoffTab = ({ values, setFieldValue }) => (
    <>
      <Typography className={classes.sectionHint}>
        {i18n.t("flowOpenAiModal.handoff.hint")}
      </Typography>
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={Boolean(values.handoffEnabled)}
            onChange={(e) => setFieldValue("handoffEnabled", e.target.checked)}
          />
        }
        label={i18n.t("flowOpenAiModal.handoff.enabled")}
      />
      <Field
        as={TextField}
        label={i18n.t("flowOpenAiModal.handoff.keywords")}
        name="handoffKeywords"
        variant="outlined"
        margin="dense"
        fullWidth
        multiline
        rows={3}
        helperText={i18n.t("flowOpenAiModal.handoff.keywordsHint")}
        disabled={!values.handoffEnabled}
      />
      <Field
        as={TextField}
        label={i18n.t("flowOpenAiModal.handoff.aiTrigger")}
        name="handoffAiTrigger"
        variant="outlined"
        margin="dense"
        fullWidth
        helperText={i18n.t("flowOpenAiModal.handoff.aiTriggerHint")}
        disabled={!values.handoffEnabled}
      />
      <Field
        as={TextField}
        label={i18n.t("flowOpenAiModal.handoff.message")}
        name="handoffMessage"
        variant="outlined"
        margin="dense"
        fullWidth
        multiline
        rows={3}
        helperText={i18n.t("flowOpenAiModal.handoff.messageHint")}
        disabled={!values.handoffEnabled}
      />
      <FormControl fullWidth margin="dense" variant="outlined">
        <InputLabel>{i18n.t("flowOpenAiModal.handoff.mode")}</InputLabel>
        <Field
          as={Select}
          label={i18n.t("flowOpenAiModal.handoff.mode")}
          name="handoffMode"
          disabled={!values.handoffEnabled}
        >
          <MenuItem value="flow">{i18n.t("flowOpenAiModal.handoff.modeFlow")}</MenuItem>
          <MenuItem value="queue">{i18n.t("flowOpenAiModal.handoff.modeQueue")}</MenuItem>
        </Field>
      </FormControl>
      {values.handoffMode === "queue" && (
        <FormControl fullWidth margin="dense" variant="outlined">
          <InputLabel>{i18n.t("flowOpenAiModal.handoff.queue")}</InputLabel>
          <Field as={Select} label={i18n.t("flowOpenAiModal.handoff.queue")} name="queueId">
            <MenuItem value={0}>{i18n.t("flowOpenAiModal.handoff.noQueue")}</MenuItem>
            {queues.map((queue) => (
              <MenuItem key={queue.id} value={queue.id}>
                {queue.name}
              </MenuItem>
            ))}
          </Field>
        </FormControl>
      )}
      <Box className={classes.sectionCard} mt={2}>
        <Typography className={classes.sectionLabel}>
          {i18n.t("flowOpenAiModal.handoff.flowTipTitle")}
        </Typography>
        <Typography className={classes.sectionHint}>
          {i18n.t("flowOpenAiModal.handoff.flowTip")}
        </Typography>
      </Box>
    </>
  );

  return (
    <div className={classes.root}>
      <Dialog
        open={open === "create" || open === "edit"}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <Formik
          initialValues={integration}
          enableReinitialize
          validationSchema={DialogflowSchema}
          onSubmit={handleSavePrompt}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form style={{ width: "100%" }}>
              <DialogContent dividers className={classes.dialogContent}>
                <Tabs
                  value={activeTab}
                  onChange={(_, value) => setActiveTab(value)}
                  className={classes.tabs}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label={i18n.t("flowOpenAiModal.tabs.general")} />
                  <Tab label={i18n.t("flowOpenAiModal.tabs.prompt")} />
                  <Tab label={i18n.t("flowOpenAiModal.tabs.handoff")} />
                </Tabs>

                {activeTab === 0 && renderGeneralTab({ touched, errors, values, setFieldValue })}
                {activeTab === 1 && renderPromptTab()}
                {activeTab === 2 && renderHandoffTab({ values, setFieldValue })}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color="secondary" variant="outlined" disabled={isSubmitting}>
                  {i18n.t("promptModal.buttons.cancel")}
                </Button>
                <Button type="submit" color="primary" variant="contained" className={classes.btnWrapper} disabled={isSubmitting}>
                  {labels.btn}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FlowBuilderOpenAIModal;
